import React, { useState } from 'react';
import { FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface FilterState {
  status: string[];
  tier: string[];
  created_from: string;
  created_to: string;
}

interface TenantFiltersProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  onClearFilters: () => void;
}

const TenantFilters: React.FC<TenantFiltersProps> = ({
  filters,
  onFiltersChange,
  onClearFilters
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const statusOptions = [
    { value: 'active', label: 'Active', color: 'text-emerald-300' },
    { value: 'trial', label: 'Trial', color: 'text-blue-300' },
    { value: 'past_due', label: 'Past Due', color: 'text-red-300' },
    { value: 'cancelled', label: 'Cancelled', color: 'text-slate-300' },
    { value: 'suspended', label: 'Suspended', color: 'text-amber-300' }
  ];

  const tierOptions = [
    { value: 'starter', label: 'Starter' },
    { value: 'professional', label: 'Professional' },
    { value: 'enterprise', label: 'Enterprise' },
    { value: 'custom', label: 'Custom' }
  ];

  const handleStatusChange = (status: string) => {
    const newStatus = filters.status.includes(status)
      ? filters.status.filter(s => s !== status)
      : [...filters.status, status];
    
    onFiltersChange({
      ...filters,
      status: newStatus
    });
  };

  const handleTierChange = (tier: string) => {
    const newTier = filters.tier.includes(tier)
      ? filters.tier.filter(t => t !== tier)
      : [...filters.tier, tier];
    
    onFiltersChange({
      ...filters,
      tier: newTier
    });
  };

  const handleDateChange = (field: 'created_from' | 'created_to', value: string) => {
    onFiltersChange({
      ...filters,
      [field]: value
    });
  };

  const getActiveFilterCount = () => {
    return filters.status.length + filters.tier.length + 
           (filters.created_from ? 1 : 0) + (filters.created_to ? 1 : 0);
  };

  const activeFilterCount = getActiveFilterCount();

  return (
    <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4 shadow-xl">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <FunnelIcon className="h-5 w-5 text-slate-400" />
          <span className="text-sm font-medium text-white">Filters</span>
          {activeFilterCount > 0 && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
              {activeFilterCount}
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          {activeFilterCount > 0 && (
            <button
              onClick={onClearFilters}
              className="text-sm text-slate-400 hover:text-slate-300 transition-colors duration-200"
            >
              Clear all
            </button>
          )}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-sm text-indigo-400 hover:text-indigo-300 transition-colors duration-200"
          >
            {isExpanded ? 'Collapse' : 'Expand'}
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="mt-4 space-y-4">
          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Subscription Status
            </label>
            <div className="flex flex-wrap gap-2">
              {statusOptions.map((option) => (
                <label key={option.value} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.status.includes(option.value)}
                    onChange={() => handleStatusChange(option.value)}
                    className="rounded border-slate-600 bg-slate-700 text-indigo-500 shadow-sm focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/50"
                  />
                  <span className={`ml-2 text-sm ${option.color}`}>
                    {option.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Tier Filter */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Subscription Tier
            </label>
            <div className="flex flex-wrap gap-2">
              {tierOptions.map((option) => (
                <label key={option.value} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.tier.includes(option.value)}
                    onChange={() => handleTierChange(option.value)}
                    className="rounded border-slate-600 bg-slate-700 text-indigo-500 shadow-sm focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/50"
                  />
                  <span className="ml-2 text-sm text-slate-300">
                    {option.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Date Range Filter */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Created Date Range
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-slate-400 mb-1">From</label>
                <input
                  type="date"
                  value={filters.created_from}
                  onChange={(e) => handleDateChange('created_from', e.target.value)}
                  className="block w-full rounded-xl border-slate-600/50 bg-slate-700/50 text-white placeholder-slate-400 shadow-sm focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/50 sm:text-sm transition-all duration-200"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">To</label>
                <input
                  type="date"
                  value={filters.created_to}
                  onChange={(e) => handleDateChange('created_to', e.target.value)}
                  className="block w-full rounded-xl border-slate-600/50 bg-slate-700/50 text-white placeholder-slate-400 shadow-sm focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/50 sm:text-sm transition-all duration-200"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Active Filters Display */}
      {activeFilterCount > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-700/50">
          <div className="flex flex-wrap gap-2">
            {filters.status.map((status) => (
              <span
                key={`status-${status}`}
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
              >
                Status: {statusOptions.find(s => s.value === status)?.label}
                <button
                  onClick={() => handleStatusChange(status)}
                  className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-emerald-500/30 transition-colors duration-200"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            ))}
            
            {filters.tier.map((tier) => (
              <span
                key={`tier-${tier}`}
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-500/20 text-purple-300 border border-purple-500/30"
              >
                Tier: {tierOptions.find(t => t.value === tier)?.label}
                <button
                  onClick={() => handleTierChange(tier)}
                  className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-purple-500/30 transition-colors duration-200"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            ))}

            {filters.created_from && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
                From: {filters.created_from}
                <button
                  onClick={() => handleDateChange('created_from', '')}
                  className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-indigo-500/30 transition-colors duration-200"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            )}

            {filters.created_to && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
                To: {filters.created_to}
                <button
                  onClick={() => handleDateChange('created_to', '')}
                  className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-indigo-500/30 transition-colors duration-200"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantFilters;